import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const EMPTY = [];

export function useProspects(params = {}) {
  const query = useQuery({
    queryKey: queryKeys.prospects.list(params),
    queryFn: () => portalApi.prospects(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000
  });
  return { rows: query.data?.prospects || EMPTY, isLoading: query.isLoading, error: query.error, refetch: query.refetch };
}

export function useCreateProspect() {
  return useApiMutation({
    mutationFn: portalApi.createProspect,
    invalidate: [queryKeys.prospects.all, queryKeys.contacts.all],
    success: (res) => `${res.prospect.name} added to Lead`,
    error: 'Could not save this inquiry'
  });
}

export function useUpdateProspect() {
  return useApiMutation({
    mutationFn: ({ id, ...changes }) => portalApi.updateProspect(id, changes),
    optimistic: ({ id, signals, ...changes }) => [
      {
        key: queryKeys.prospects.lists(),
        update: (data) => ({
          ...data,
          prospects: data.prospects.map((p) =>
            p._id === id ? { ...p, ...changes, signals: { ...p.signals, ...signals } } : p
          )
        })
      }
    ],
    invalidate: [queryKeys.prospects.all, queryKeys.contacts.all],
    error: 'Could not update this prospect'
  });
}

export function useDeleteProspect() {
  return useApiMutation({
    mutationFn: (id) => portalApi.removeProspect(id),
    optimistic: (id) => [
      { key: queryKeys.prospects.lists(), update: (data) => ({ ...data, prospects: data.prospects.filter((p) => p._id !== id) }) }
    ],
    invalidate: [queryKeys.prospects.all, queryKeys.scheduling.all],
    success: 'Prospect deleted',
    error: 'Could not delete this prospect'
  });
}

export function useBookTour() {
  return useApiMutation({
    mutationFn: ({ id, start }) => portalApi.bookProspectTour(id, start),
    invalidate: [queryKeys.prospects.all, queryKeys.scheduling.bookings(), queryKeys.scheduling.availabilities()],
    success: (res) => res.warning || 'Tour booked',
    error: 'Could not book this tour'
  });
}

export function useConvertProspect() {
  return useApiMutation({
    mutationFn: ({ id, ...payload }) => portalApi.convertProspect(id, payload),
    invalidate: [queryKeys.prospects.all, queryKeys.clients.all, queryKeys.properties.all],
    error: 'Could not convert this prospect'
  });
}
